import type {
  AgentItem,
  AttentionItem,
  NoteItem,
  ReminderItem,
  ViewMode,
} from '../../domain/types';
import { countRadarWorkThreads } from '../../domain/radarThreads';
import { CONNECTABLE_SOURCES, type ConnectableSource } from '../../domain/providerIds';
import { SOURCE_PROVIDERS } from '../../domain/providerRegistry';

/** Provider-derived view titles, plus the fixed radar/agents/wiki entries. */
export const VIEW_TITLES: Record<ViewMode, { title: string; subtitle: string }> = {
  radar: {
    title: 'Radar de Atención',
    subtitle: 'Tus tareas y señales vivas agrupadas por acción inmediata',
  },
  notifications: {
    title: 'Notificaciones',
    subtitle: 'Qué ocurrió y dónde atenderlo',
  },
  agents: {
    title: 'Agentes Autónomos',
    subtitle: 'Observabilidad de sesiones de código, ramas, tokens y costes',
  },
  wiki: {
    title: 'Knowledge Wiki',
    subtitle: 'El contexto que permanece, se conecta y explica tu Radar',
  },
  ...Object.fromEntries(
    CONNECTABLE_SOURCES.map((id) => {
      const def = SOURCE_PROVIDERS[id];
      return [def.view, { title: def.navLabel, subtitle: def.viewSubtitle }];
    }),
  ),
} as Record<ViewMode, { title: string; subtitle: string }>;

export type SourceCounts = {
  radar: number;
  notifications: number;
  wiki: number;
  agents: number;
  reminders: number;
  notes: number;
} & Record<ConnectableSource, number>;

export function buildSourceCounts(
  items: AttentionItem[],
  agents: AgentItem[],
  reminders: ReminderItem[],
  notes: NoteItem[],
  wikiCount = 0,
  notificationCount = 0,
): SourceCounts {
  const sourceCounts = Object.fromEntries(CONNECTABLE_SOURCES.map((id) => [id, 0])) as Record<
    ConnectableSource,
    number
  >;
  for (const item of items) {
    const id = item.source as ConnectableSource;
    if (!(id in sourceCounts)) continue;
    if (SOURCE_PROVIDERS[id].countsAsAttention(item)) sourceCounts[id]++;
  }
  return {
    radar: countRadarWorkThreads(items, agents),
    notifications: notificationCount,
    wiki: wikiCount,
    ...sourceCounts,
    agents: agents.length,
    reminders: reminders.filter((r) => !r.done).length,
    notes: notes.length,
  };
}

/**
 * Compact inspector visibility depends only on an explicit "Ver contexto"
 * activation, never on mere item selection/expansion/deep-linking — see
 * openspec/changes/provider-item-interaction-ux.
 */
export function isInspectorSheetOpen(
  isCompact: boolean,
  isContextSurfaceOpen: boolean | undefined,
): boolean {
  return Boolean(isCompact && isContextSurfaceOpen);
}
